import React, { useState, useEffect, useRef } from 'react';
import { useDraggable } from '@dnd-kit/core';
import {
  Box, Typography, TextField, Chip, Divider,
  InputAdornment, Badge, Collapse, IconButton,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PeopleIcon from '@mui/icons-material/People';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import TableBarIcon from '@mui/icons-material/TableBar';
import { getCategoryColor, getCategoryLabel, getRsvpSuffix } from './constants';

/**
 * Right panel: list of unassigned guests that can be dragged onto tables,
 * plus collapsible per-table guest lists.
 *
 * Props:
 *   unassignedGuests – WeddingGuest[]
 *   allGuests        – WeddingGuest[] (for stats)
 *   tables           – enriched table[] (for per-table lists)
 *   activeFilter
 *   onClose          – ha meg van adva, bezárás (X) gomb jelenik meg a fejlécben (mobil drawer)
 *   stacked          – álló módban (telefon/tablet): teljes szélesség, felső szegély a bal oldali helyett
 *   gridColumns      – stacked módban hány oszlopban jelenjenek meg a nem ültetett vendégek (pl. telefonon 2, tableten 3)
 *   collapsed        – stacked módban: ha true, a panel a fejlécére csukódik össze (a canvas-nak ad helyet)
 *   onToggleCollapse – ha meg van adva, egy le/fel nyíl gomb jelenik meg a fejlécben a collapsed váltásához
 */
export default function GuestListPanel({ unassignedGuests, allGuests, totalCompanions = 0, tables = [], activeFilter, onClose, stacked = false, gridColumns = 3, collapsed = false, onToggleCollapse }) {
  const [search, setSearch] = useState('');
  const [expandedTables, setExpandedTables] = useState({});
  // Stacked (tablet álló) nézetben a kereső egy ikon mögé rejtve indul, hogy minél előbb
  // maga a lista látszódjon — kattintásra nyílik ki.
  const [searchOpen, setSearchOpen] = useState(false);
  const searchInputRef = useRef(null);
  useEffect(() => {
    if (searchOpen) searchInputRef.current?.focus();
  }, [searchOpen]);

  const filtered = unassignedGuests.filter(g => {
    const name = `${g.lastName} ${g.firstName}`.toLowerCase();
    return name.includes(search.toLowerCase());
  });

  // Count all assigned people (regular guests + companions)
  const totalPeople = allGuests.length + totalCompanions;
  const assignedCount = totalPeople - unassignedGuests.length;

  const toggleTable = (tableId) => {
    setExpandedTables(prev => ({ ...prev, [tableId]: !prev[tableId] }));
  };

  const tablesWithGuests = tables.filter(t => (t.guests?.length ?? 0) > 0);

  return (
    <Box
      sx={{
        // sm–md között (tablet) fokozatosan szélesedik, hogy a vászonnak is maradjon hely.
        width: stacked ? '100%' : { xs: '100%', sm: 'clamp(160px, 26vw, 240px)', md: 240 },
        flexShrink: 0,
        borderLeft: stacked ? 'none' : '1px solid',
        borderTop: stacked ? '1px solid' : 'none',
        borderColor: 'divider',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        bgcolor: '#fafafa',
      }}
    >
      {/* Header */}
      <Box sx={{ p: stacked ? 1 : 1.5, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: stacked ? 0 : 1 }}>
          <PeopleIcon sx={{ color: '#2a5d32', fontSize: 18 }} />
          <Typography variant="subtitle2" fontWeight={700}>
            Nem ültetett
          </Typography>
          {stacked && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
              ({assignedCount}/{totalPeople} ültetve)
            </Typography>
          )}
          <Badge badgeContent={unassignedGuests.length} color="warning" sx={{ ml: 'auto' }} />
          {stacked && !collapsed ? (
            <IconButton size="small" onClick={() => setSearchOpen(o => !o)} sx={{ ml: 0.5 }}>
              {searchOpen ? <CloseIcon fontSize="small" /> : <SearchIcon fontSize="small" />}
            </IconButton>
          ) : null}
          {onToggleCollapse && (
            <IconButton size="small" onClick={onToggleCollapse} sx={{ ml: 0.5 }} title={collapsed ? 'Vendéglista megnyitása' : 'Vendéglista becsukása'}>
              {collapsed ? <ExpandMoreIcon fontSize="small" /> : <ExpandLessIcon fontSize="small" />}
            </IconButton>
          )}
          {onClose && (
            <IconButton size="small" onClick={onClose} sx={{ ml: 0.5 }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
        {!stacked && (
          <Typography variant="caption" color="text.secondary">
            {assignedCount}/{totalPeople} személy ültetve
          </Typography>
        )}
        {collapsed ? null : stacked ? (
          <Collapse in={searchOpen}>
            <TextField
              inputRef={searchInputRef}
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Nem ültetettek szűrése…"
              size="small"
              fullWidth
              sx={{ mt: 1 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 16 }} />
                  </InputAdornment>
                ),
              }}
            />
          </Collapse>
        ) : (
          <TextField
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Nem ültetettek szűrése…"
            size="small"
            fullWidth
            sx={{ mt: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 16 }} />
                </InputAdornment>
              ),
            }}
          />
        )}
      </Box>

      {/* Scrollable area: unassigned guests + table lists — összecsukott állapotban rejtve */}
      {!collapsed && (
      <Box sx={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
        {/* Unassigned guest list — tablet (stacked) nézetben rács, mert ott a teljes szélesség
            rendelkezésre áll, és egy sorban simán elfér 3 név, így egyszerre sokkal több
            vendég látszik görgetés nélkül, mint egy egyoszlopos listánál. */}
        <Box sx={stacked
          ? { p: 1, display: 'grid', gridTemplateColumns: `repeat(${gridColumns}, 1fr)`, gap: 0.5 }
          : { p: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }
        }>
          {filtered.length === 0 ? (
            <Typography variant="caption" color="text.secondary" sx={{ p: 1, textAlign: 'center', gridColumn: stacked ? '1 / -1' : undefined }}>
              {search ? 'Nincs találat' : 'Minden vendég ültetve van!'}
            </Typography>
          ) : (
            filtered.map(guest => (
              <DraggableGuestItem key={guest.id} guest={guest} activeFilter={activeFilter} compact={stacked} />
            ))
          )}
        </Box>

        {/* Per-table guest lists */}
        {tablesWithGuests.length > 0 && (
          <>
            <Divider sx={{ mt: 1 }} />
            <Box sx={{ p: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                <TableBarIcon sx={{ fontSize: 14, color: '#2a5d32' }} />
                <Typography variant="caption" fontWeight={700} color="text.secondary">
                  Asztalok ({tablesWithGuests.length})
                </Typography>
              </Box>
              {tablesWithGuests.map(table => (
                <Box key={table.id} sx={{ mb: 0.5 }}>
                  <Box
                    onClick={() => toggleTable(table.id)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      p: '3px 6px',
                      borderRadius: 1,
                      cursor: 'pointer',
                      bgcolor: expandedTables[table.id] ? '#e8f5e9' : 'transparent',
                      '&:hover': { bgcolor: '#e8f5e9' },
                      transition: 'background 0.15s',
                    }}
                  >
                    <Typography variant="caption" sx={{ fontSize: 11, fontWeight: 600, flex: 1 }}>
                      {table.tableName}
                      <Typography component="span" variant="caption" sx={{ fontSize: 10, color: 'text.secondary', ml: 0.5 }}>
                        ({table.guests?.length ?? 0}/{table.capacity})
                      </Typography>
                    </Typography>
                    {expandedTables[table.id] ? (
                      <ExpandLessIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                    ) : (
                      <ExpandMoreIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                    )}
                  </Box>
                  <Collapse in={expandedTables[table.id]}>
                    <Box sx={{ pl: 1.5, py: 0.5, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                      {[...(table.guests ?? [])]
                        .sort((a, b) => (a.seatNumber ?? 99) - (b.seatNumber ?? 99))
                        .map(g => (
                          <Typography key={g.id} variant="caption" sx={{ fontSize: 10, lineHeight: 1.5, color: '#424242' }}>
                            {g.seatNumber ? `${g.seatNumber}.\u00a0` : '\u2013\u00a0'}
                            {g.lastName} {g.firstName}
                            {g.isCompanion && <Chip label="K" size="small" sx={{ fontSize: 8, height: 14, ml: 0.5, bgcolor: '#e3f2fd', color: '#1565c0' }} />}
                            <Typography component="span" variant="caption" sx={{ fontSize: 10, color: 'warning.main' }}>
                              {getRsvpSuffix(g.rsvpStatus)}
                            </Typography>
                          </Typography>
                        ))
                      }
                    </Box>
                  </Collapse>
                </Box>
              ))}
            </Box>
          </>
        )}
      </Box>
      )}

      {!stacked && !collapsed && (
        <>
          <Divider />
          <Box sx={{ p: 1, textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              Húzz egy vendéget az asztalra
            </Typography>
          </Box>
        </>
      )}
    </Box>
  );
}

function DraggableGuestItem({ guest, activeFilter, compact = false }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `guest-panel-${guest.id}`,
    data: { type: 'GUEST', guestId: guest.id },
  });

  const catColor = getCategoryColor(activeFilter === 'none' ? 'default' : (guest.guestCategory || 'default'));
  const fullName = `${guest.lastName} ${guest.firstName}`;

  return (
    <Box
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      title={compact ? fullName : undefined}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        p: '4px 6px',
        minWidth: 0,
        borderRadius: 1.5,
        cursor: 'grab',
        // A vendég chipek görgethető listában/rácsban ülnek — touch-action nélkül a natív
        // görgetés "elviszi" az érintést a dnd-kit pointer-drag elől mobilon.
        touchAction: 'none',
        opacity: isDragging ? 0.4 : 1,
        bgcolor: '#fff',
        border: '1px solid #e0e0e0',
        '&:hover': { bgcolor: '#e8f5e9', borderColor: '#a5d6a7' },
        transition: 'all 0.12s',
      }}
    >
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          bgcolor: catColor,
          flexShrink: 0,
        }}
      />
      {/* Rács (compact) nézetben egy sorban 3 fér el — a névnek ezért el kell férnie
          csonkolva is, a kísérő/kategória chipek helyett csak egy apró jelzés marad. */}
      <Typography
        variant="caption"
        title={fullName}
        sx={compact
          ? { fontSize: 12.5, lineHeight: 1.3, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }
          : { fontSize: 12.5, lineHeight: 1.3 }
        }
      >
        {fullName}
      </Typography>
      {compact ? (
        guest.isCompanion && (
          <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#1565c0', flexShrink: 0 }} />
        )
      ) : guest.isCompanion ? (
        <Chip
          label="Kísérő"
          size="small"
          sx={{ fontSize: 9, height: 16, bgcolor: '#e3f2fd', color: '#1565c0', ml: 'auto', flexShrink: 0 }}
        />
      ) : guest.guestCategory && activeFilter !== 'none' ? (
        <Chip
          label={getCategoryLabel(guest.guestCategory)}
          size="small"
          sx={{ fontSize: 9, height: 16, bgcolor: catColor + '40', ml: 'auto', flexShrink: 0 }}
        />
      ) : null}
    </Box>
  );
}
